import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { notification } from "antd";
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc, runTransaction } from "firebase/firestore";
import { db } from "../data/base";
import ModelContainerFrontEnd from "../components/ModelViewer/ModelContainerFrontEnd";
import ProjectModelViewer from "../components/ModelViewer/ProjectModelViewer";
import CardModelView from "../components/ModelViewer/CardModelView";
import MenuContainer from "../components/ModelViewer/MenuContainer";
import Password from "../components/ModelViewer/Password";
import { brandViewerActions } from "../redux/brandViewer/brandViewer";
import { modelGlassesActions } from "../redux/brandViewer/modelGlasses";
import ThreeJSViewer from "../components/ModelViewer/ThreeJSViewer/ThreeJSViewer";
import axios from "axios";
import Cookies from "js-cookie";
import { v4 as uuidv4 } from "uuid";
import logoSorry from "../assets/icon/ARShades_Logo_Sorry.svg"

/**
 * ARShades3dsViewer - Componente per visualizzare modelli 3D con multiple varianti
 * Gestisce la visualizzazione di occhiali 3D con menu di selezione varianti e tracciamento sessioni
 */
const ARShades3dsViewer = ({
    isEditModeOn,
    containerWidth,
    containerHeight,
    setCurrentVariant = () => { },
    _setListVariant = () => { },
}) => {
    // === ROUTER & REDUX ===
    const params = useParams();
    const dispatch = useDispatch();
    const location = useLocation();

    // Controllo del parametro legacy nell'URL per decidere quale visualizzatore utilizzare
    const queryParams = new URLSearchParams(location.search);
    const useLegacyViewer = queryParams.get("legacy") === "true";

    // Parametri URL
    const tViewer = params.TokenViewer;
    const product = params.TokenCatalogProduct;

    // === STATI PRINCIPALI ===
    // Dati del visualizzatore e modelli
    const [idViewer, setIdViewer] = useState();
    const [listVariant, setListVariant] = useState([]);
    const [initialModel, setInitialModel] = useState();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isCatalogExpired, setIsCatalogExpired] = useState(false);
    const [modelFromCache, setModelFromCache] = useState(null);

    // Sicurezza e accesso
    const [lock, setLock] = useState(false);
    const [pwd, setPwd] = useState("");

    // UI e posizionamento
    const [positionMenu, setPositionMenu] = useState("bottom");

    // Timing e history (alcune di queste potrebbero essere ottimizzabili)
    const [initialTime, setInitialTime] = useState();
    const [initialModelTime, setInitiaModellTime] = useState(new Date());
    const [modelHistory, setModelHistory] = useState([]);

    // Stati per la sessione Firestore
    const [sessionRef, setSessionRef] = useState(null);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [isWebView, setIsWebView] = useState(false);
    const [listCreated, setListCreated] = useState(false);

    // Riferimenti Firestore 
    const refBrand = useRef(null);
    
    // Riferimento per tracciare i modelli che hanno già aggiornato i contatori
    const processedModelsRef = useRef(new Set());

    // === SELETTORI REDUX ===
    const project = useSelector((state) => state.brandViewer);
    const modelInView = useSelector((state) =>
        state.modelGlasses.find((model) => model.id === initialModel)
    );

    // === STRUTTURA SESSIONE ===
    const [session, setSession] = useState({
        deviceId: "",
        device: "",
        deviceOs: "",
        isFirstSession: false,
        catalogRef: "",
        sessionStartDate: "",
        sessionEndDate: "",
        list_catalog_product_refs: [],
        list_viewed_products: [],
        list_catalog_variant_refs: [],
        list_line_tag_refs: [],
        catalogProductRefsDoc: null,
        totalTime: 0,
        list_size_refs: [],
        referrer: "",
    });

    // === UTILITY FUNCTIONS ===
    /**
     * Formatta una data nel formato utilizzato per le sessioni
     */
    const formatSessionDate = (date) => {
        const padWithZero = (num) => num.toString().padStart(2, '0');
        return `${date.getFullYear()}/${padWithZero(date.getMonth() + 1)}/${padWithZero(date.getDate())} ${padWithZero(date.getHours())}:${padWithZero(date.getMinutes())}:${padWithZero(date.getSeconds())}`;
    };

    /**
     * Ottiene il mese corrente (1-12)
     */
    const getCurrentMonth = () => new Date().getMonth() + 1;

    /**
     * Ottiene l'anno corrente
     */
    const getCurrentYear = () => new Date().getFullYear();

    /**
     * Aggiorna il riferimento al brand
     */
    const updateRefBrand = (brandDocRef) => {
        refBrand.current = brandDocRef;
    };

    function removeUnwantedFields(obj, fieldsToRemove = ['lastUpdate']) {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(item => removeUnwantedFields(item, fieldsToRemove));
        const newObj = {};
        for (const key in obj) {
            if (fieldsToRemove.includes(key)) continue;
            if (typeof obj[key] === 'object') {
                newObj[key] = removeUnwantedFields(obj[key], fieldsToRemove);
            } else {
                newObj[key] = obj[key];
            }
        }
        return newObj;
    }

    // === EFFECTS PER L'INIZIALIZZAZIONE ===

    /**
     * Effect: Imposta la posizione del menu basata sui dati del progetto
     */
    useEffect(() => {
        setPositionMenu(project[0]?.menuPosition);
    }, [project[0]]);

    /**
     * Effect: Carica i dati del visualizzatore 3D
     */
    useEffect(() => {
        const fetchViewerData = async () => {
            try {
                // Prova prima con l'ID diretto
                const viewerDocRef = doc(collection(db, "3DViewers"), tViewer);
                let viewerDoc = await getDoc(viewerDocRef);

                // Fallback: cerca per campo catalog se non trovato
                if (!viewerDoc.exists() || !viewerDoc.data()) {
                    const viewersQuery = query(
                        collection(db, "3DViewers"),
                        where("catalog", "==", tViewer)
                    );
                    const querySnapshot = await getDocs(viewersQuery);

                    if (!querySnapshot.empty) {
                        viewerDoc = querySnapshot.docs[0];
                    } else {
                        console.error("Visualizzatore non trovato");
                        setError("3D Viewer not found or missing data");
                        return;
                    }
                }

                const data = viewerDoc.data();
                setIdViewer(tViewer);
                const cleanData = removeUnwantedFields(data, ['lastUpdate']);
                dispatch(brandViewerActions.setSelectedProject(cleanData));
                setInitialTime(new Date());
            } catch (error) {
                console.error("Errore durante il recupero del visualizzatore:", error);
                setError("An error occurred while loading the 3D viewer");
            }
        };

        fetchViewerData();
    }, [tViewer, dispatch]);

    /**
     * Effect: Carica i dati del prodotto e del modello iniziale
     */
    useEffect(() => {
        const fetchGlassesAndModelData = async () => {
            if (!idViewer) return;

            try {
                // Verifica se il catalogo è scaduto PRIMA di caricare il modello
                const docRef = await getDoc(doc(db, "3DViewers", idViewer));
                const docCatalog = docRef.data().catalog;
                const catalogRefDoc = doc(db, "Catalogues", docCatalog);
                const catalogRefSnap = await getDoc(catalogRefDoc);
                
                if (!catalogRefSnap.exists()) {
                    console.error("Error: catalog not found!");
                    setError('Catalog not found.');
                    return;
                }
                
                if (catalogRefSnap.data().isExpired === true) {
                    console.error("Error: catalog is expired!");
                    setError('ARSHADES license has expired or is inactive.');
                    setIsCatalogExpired(true);
                    return;
                }

                // Recupera il documento del prodotto
                const productDocRef = doc(
                    collection(db, "3DViewers", idViewer, "Products"),
                    product
                );
                const productDoc = await getDoc(productDocRef);

                if (!productDoc.exists()) {
                    console.error("Product non trovato");
                    setError("The product you're looking for was not found. Please check the link and try again.");
                    return;
                }

                const productData = productDoc.data();
                setListVariant(productData.list_variants);
                setInitialModel(productData.initialModel);

                // Carica il modello iniziale e le sue animazioni
                const variantDocRef = doc(collection(db, "CataloguesVariants"), productData.initialModel);
                const variantDoc = await getDoc(variantDocRef);

                if (variantDoc.exists()) {
                    const variantData = variantDoc.data();
                    setModelHistory((prevHistory) => [...prevHistory, variantData.urlGlobalComplete]);
                } else {
                    console.error("Modello non trovato");
                    setError("The 3D model was not found in our database.");
                }
            } catch (error) {
                console.error("Errore durante il recupero dei dati:", error);
                setError("An error occurred while retrieving the product data. Please try again later.");
            }
        };

        fetchGlassesAndModelData();
    }, [idViewer, product]);

    /**
     * Effect: Verifica lo stato di lock del visualizzatore
     */
    useEffect(() => {
        if (!idViewer) return;

        const fetchViewerLockData = async () => {
            try {
                const docRef = doc(collection(db, "3DViewers"), idViewer);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setLock(data.lock || false);
                    if (data.lock) {
                        setPwd(data.password || "");
                    }
                }
            } catch (error) {
                console.error("Errore durante il controllo del lock:", error);
                setLock(false);
            }
        };

        fetchViewerLockData();
    }, [idViewer]);

    /**
     * Effect: Carica tutte le varianti e popola list_catalog_variant_refs
     */
    useEffect(() => {
        const fetchVariants = async () => {
            if (!listVariant?.length) return;

            try {
                // Carica i dati di tutte le varianti
                await Promise.all(
                    listVariant.map(async (v) => {
                        const variantRef = doc(collection(db, "CataloguesVariants"), v);
                        const variantSnap = await getDoc(variantRef);

                        if (variantSnap.exists()) {
                            const variantData = variantSnap.data();
                            const cleanVariantData = removeUnwantedFields(variantData, ['lastUpdate']);
                            dispatch(
                                modelGlassesActions.setModel({ data: cleanVariantData, id: v })
                            );
                        } else {
                            console.error(`Variante ${v} non trovata`);
                        }
                    })
                );

                // Popola list_catalog_variant_refs con gli ID delle varianti
                setSession(prevSession => ({
                    ...prevSession,
                    list_catalog_variant_refs: [...listVariant]
                }));
            } catch (error) {
                console.error("Errore durante il recupero delle varianti:", error);
            }
        };

        fetchVariants();
    }, [listVariant, dispatch]);

    // === EFFECTS PER LA GESTIONE DELLA SESSIONE ===

    /**
     * Effect: Inizializzazione completa della sessione
     */
    useEffect(() => {
        if (!idViewer || isCatalogExpired) return;

        const initializeSession = async () => {
            try {
                // Ottieni device ID
                const getDeviceID = async () => {
                    try {
                        const res = await axios.get("https://api.ipify.org/?format=json");
                        return res.data.ip;
                    } catch (error) {
                        console.error("Failed to fetch IP address:", error);
                        return uuidv4();
                    }
                };
                const deviceID = await getDeviceID();

                // Formatta data corrente
                const getCurrentDateTime = () => {
                    const padWithZero = (num) => num.toString().padStart(2, "0");
                    const date = new Date();
                    return {
                        year: date.getFullYear(),
                        month: padWithZero(date.getMonth() + 1),
                        day: padWithZero(date.getDate()),
                        hours: padWithZero(date.getHours()),
                        minutes: padWithZero(date.getMinutes()),
                        seconds: padWithZero(date.getSeconds()),
                        date,
                    };
                };
                const { year, month, day, hours, minutes, seconds, date } = getCurrentDateTime();

                // Recupera dati del catalog e contatori
                const getCatalogAndCounters = async () => {
                    const viewerDocRef = doc(db, "3DViewers", idViewer);
                    const viewerDocSnap = await getDoc(viewerDocRef);
                    if (!viewerDocSnap.exists()) throw new Error("Visualizzatore non trovato");

                    const docCatalog = viewerDocSnap.data().catalog;
                    const catalogDocRef = doc(db, "Catalogues", docCatalog);
                    const catalogDocSnap = await getDoc(catalogDocRef);
                    if (!catalogDocSnap.exists()) throw new Error("Catalog non trovato");

                    const docDataConsumption = catalogDocSnap.data().dataConsumption;
                    const contatoriDocRef = doc(db, "Contatori_Brand", docDataConsumption);
                    const contatoriDocSnap = await getDoc(contatoriDocRef);
                    if (!contatoriDocSnap.exists()) throw new Error("Contatori non trovati");

                    return { catalogDocRef, contatoriDocRef, contatoriDocSnap };
                };
                const { catalogDocRef, contatoriDocRef, contatoriDocSnap } = await getCatalogAndCounters();
                const contatoriData = contatoriDocSnap.data();

                // Recupera dati del prodotto
                const getProductData = async () => {
                    const productDocRef = doc(db, "CataloguesProducts", product);
                    const productDocSnap = await getDoc(productDocRef);
                    if (!productDocSnap.exists()) throw new Error("Product non trovato");

                    return { productDocRef, productDocSnap };
                };
                const { productDocRef, productDocSnap } = await getProductData();
                const productData = productDocSnap.data();

                // Salva riferimenti per uso futuro se necessario
                updateRefBrand(catalogDocRef);

                // Determina il browser
                const determineBrowserName = () => {
                    const userAgent = navigator.userAgent;
                    if (userAgent.includes("Firefox")) return "Mozilla Firefox";
                    if (userAgent.includes("Chrome")) return "Google Chrome";
                    if (userAgent.includes("Safari")) return "Apple Safari";
                    if (userAgent.includes("Opera") || userAgent.includes("OPR")) return "Opera";
                    if (userAgent.includes("Trident")) return "Microsoft Internet Explorer";
                    if (userAgent.includes("Edge")) return "Microsoft Edge";
                    return "Unknown";
                };

                // Gestisci la prima sessione
                const isFirstSession = !Cookies.get("visited");
                if (isFirstSession) {
                    Cookies.set("visited", "true");
                }

                // Rileva WebView
                const userAgent = navigator.userAgent;
                const isWV = /\bwv\b/.test(userAgent);
                setIsWebView(isWV);

                // Aggiorna lo stato della sessione
                setSession(prevSession => ({
                    ...prevSession,
                    deviceId: deviceID,
                    device: navigator.userAgent,
                    deviceOs: determineBrowserName(),
                    isFirstSession: isFirstSession,
                    catalogRef: catalogDocRef.id,
                    sessionStartDate: `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`,
                    list_catalog_product_refs: [productDocRef.id],
                    list_line_tag_refs: productData.list_line_tags[0] ? [productData.list_line_tags[0]] : [],
                    catalogProductRefsDoc: productDocRef.id,
                    list_size_refs: [], // Sarà popolato quando le varianti vengono caricate
                    referrer: document.referrer.trim() !== '' ? document.referrer : 'UNKNOWN',
                }));

                // Aggiorna i contatori se necessario
                const updateCountersIfNeeded = async () => {
                    const sameMonthYear = contatoriData.bandwidth_3dviewer.some(
                        (bandw) => bandw.month === date.getMonth() + 1 && bandw.year === year
                    );
                    if (!sameMonthYear) {
                        // Utilizzo transazione atomica per aggiungere nuovo record mese/anno
                        console.log("🟦 ARShades3dsViewer (multi-viewer): Creazione nuovo record mese/anno", { month: date.getMonth() + 1, year });
                        try {
                            await runTransaction(db, async (transaction) => {
                                const contatoriDoc = await transaction.get(contatoriDocRef);
                                if (!contatoriDoc.exists()) {
                                    console.error("Error: contatori document not found!");
                                    return;
                                }

                                const currentData = contatoriDoc.data();
                                if (!currentData.bandwidth_3dviewer) {
                                    console.error("Error: bandwidth_3dviewer field not found!");
                                    return;
                                }

                                // Ricontrolla se il record esiste (potrebbero averlo aggiunto altri utenti nel frattempo)
                                const existingRecord = currentData.bandwidth_3dviewer.find(
                                    bandw => bandw.month === date.getMonth() + 1 && bandw.year === year
                                );

                                if (!existingRecord) {
                                    transaction.update(contatoriDocRef, {
                                        bandwidth_3dviewer: [
                                            ...currentData.bandwidth_3dviewer,
                                            { bandwidth_month: 0, calls_number: 0, month: date.getMonth() + 1, year }
                                        ]
                                    });
                                }
                            });
                        } catch (error) {
                            console.error("Errore durante l'aggiunta del nuovo record contatori:", error);
                        }
                    }
                };
                await updateCountersIfNeeded();
            } catch (error) {
                console.error("Errore durante l'inizializzazione della sessione:", error);
            }
        };

        initializeSession();
    }, [idViewer, product, isCatalogExpired]);

    /**
     * Effect: Aggiorna cronologia modelli (potrebbe essere semplificato)
     */
    useEffect(() => {
        const viewer = document.querySelector("model-viewer");
        if (viewer?.src) {
            setModelHistory((prevHistory) => [...prevHistory, viewer?.src]);
        }
    }, []);

    // Effetto per resettare la ref al cambio di sessione
    useEffect(() => {
        if (session.deviceId) return;
        processedModelsRef.current = new Set();
        // console.log("🔄 Reset processedModelsRef per nuova sessione");
    }, [session.deviceId]); // o altro identificativo della sessione

    useEffect(() => {
        // console.log("🔄 Reset modelFromCache per nuovo modello:", initialModel);
        setModelFromCache(null);
    }, [initialModel]);

    /**
     * Effect: Gestisce bandwidth e creazione iniziale di list_viewed_products
     */
    useEffect(() => {
        if (modelFromCache === null) return;
        if (!idViewer || !initialModel || lock) return;
        
        // console.log("🟦 processedModelsRef PRIMA", processedModelsRef.current);
        
        // Verifica se questo modello è già stato processato per evitare duplicazioni
        const modelKey = `${idViewer}-${initialModel}`;
        
        // Se il modello è già stato processato, non fare nulla
        if (processedModelsRef.current.has(modelKey)) {
            // console.log("🟡 Modello già processato, skipping:", modelKey);
            return;
        }

        const updateViewerBandwidth = async () => {
            try {
                // Recupera dati del modello e calcola bandwidth
                const variantRef = doc(collection(db, "CataloguesVariants"), initialModel);
                const variantSnap = await getDoc(variantRef);
                if (!variantSnap.exists()) throw new Error("Modello non trovato");

                const src = variantSnap.data().urlGlobalComplete;
                const fileImg = await fetch(src).then((r) => r.blob());
                const sizeMb = fileImg.size / 1000000;
                const banda = parseFloat(sizeMb.toFixed(2)) + 0.5;

                // Aggiorna contatori bandwidth
                const viewerRef = doc(collection(db, "3DViewers"), idViewer);
                const viewerSnap = await getDoc(viewerRef);
                if (!viewerSnap.exists()) throw new Error("Visualizzatore non trovato");

                const docCatalog = viewerSnap.data().catalog;
                const catalogRef = doc(collection(db, "Catalogues"), docCatalog);
                const catalogSnap = await getDoc(catalogRef);
                if (!catalogSnap.exists()) throw new Error("Catalog non trovato");

                const dataConsumptionDocId = catalogSnap.data().dataConsumption;
                const meseCorrente = getCurrentMonth();
                const year = getCurrentYear();

                // Aggiorna bandwidth solo se il modello è stato scaricato da internet (non da cache)
                // Il controllo per evitare duplicazioni è già fatto sopra con processedModelsRef
                const shouldUpdateBandwidth = modelFromCache === false;
                
                if (shouldUpdateBandwidth) {
                    // Utilizzo transazione per gestire atomicamente i contatori
                    // console.log("🟦 ARShades3dsViewer (multi-viewer): Aggiornamento contatori bandwidth", { banda, meseCorrente, year });
                    const contatoriRef = doc(db, "Contatori_Brand", dataConsumptionDocId);
                    try {
                        await runTransaction(db, async (transaction) => {
                            const contatoriDoc = await transaction.get(contatoriRef);
                            if (!contatoriDoc.exists()) {
                                throw new Error("Contatori non trovati");
                                return; // Esce dalla transazione
                            }

                            const currentData = contatoriDoc.data();
                            if (!currentData.bandwidth_3dviewer) {
                                console.error("Error: bandwidth_3dviewer field not found!");
                                return; // Esce dalla transazione
                            }

                            const updatedBandwidth = currentData.bandwidth_3dviewer.map((ele) => {
                                if (meseCorrente === ele.month && year === ele.year) {
                                    return {
                                        bandwidth_month: ele.bandwidth_month + banda,
                                        calls_number: ele.calls_number + 1,
                                        month: ele.month,
                                        year: ele.year,
                                    };
                                }
                                return ele;
                            });

                            transaction.update(contatoriRef, {
                                bandwidth_3dviewer: updatedBandwidth,
                            });
                        });
                    } catch (error) {
                        console.error("Errore durante l'aggiornamento dei contatori:", error);
                        // Qui potresti implementare un fallback o retry logic
                    }
                } else {
                    // console.log("🟢 ARShades3dsViewer: Modello caricato da cache - bandwidth non aggiornata", { modelFromCache, modelKey });
                }

                // Crea o aggiorna list_viewed_products
                if (!listCreated) {
                    const variantData = variantSnap.data();
                    const date = new Date();
                    const timeDiff = Math.floor((date.getTime() - initialTime.getTime()) / 1000);
                    const variantSize = variantData?.size?.size || "";

                    setSession(prevSession => {
                        let updatedSizeRefs = [...prevSession.list_size_refs];
                        
                        if (variantSize && !updatedSizeRefs.includes(variantSize)) {
                            updatedSizeRefs.push(variantSize);
                        }

                        return {
                            ...prevSession,
                            list_viewed_products: [{
                                productName: variantData?.glassesName || "",
                                lineTagRef: prevSession.list_line_tag_refs[0] || "",
                                catalogProductRef: prevSession.catalogProductRefsDoc || "",
                                list_tried_variants: [{
                                    catalogVariantRefs: initialModel,
                                    timeStart: initialTime,
                                    totalSeconds: timeDiff,
                                    sizeRef: variantSize,
                                }],
                                timeStart: initialTime,
                                consumedBandwidth: banda,
                                date: initialTime,
                                totalSeconds: timeDiff,
                            }],
                            list_size_refs: updatedSizeRefs
                        };
                    });
                    setListCreated(true);
                } else {
                    // Aggiorna bandwidth per varianti successive
                    const variantData = variantSnap.data();
                    
                    setSession(prevSession => {
                        const updatedViewedProducts = [...prevSession.list_viewed_products];
                        if (updatedViewedProducts[0]) {
                            updatedViewedProducts[0] = {
                                ...updatedViewedProducts[0],
                                consumedBandwidth: updatedViewedProducts[0].consumedBandwidth + banda,
                                productName: variantData?.glassesName || updatedViewedProducts[0].productName
                            };
                        }
                        
                        return {
                            ...prevSession,
                            list_viewed_products: updatedViewedProducts
                        };
                    });
                }

                // Marca il modello come processato per evitare aggiornamenti duplicati
                const currentModelKey = `${idViewer}-${initialModel}`;
                processedModelsRef.current.add(currentModelKey);
                // console.log("✅ Modello processato con successo:", currentModelKey);
                // console.log("🟦 processedModelsRef DOPO", processedModelsRef.current);
                
                // Reset dello stato per evitare race conditions con il prossimo modello
                setModelFromCache(null);
            } catch (error) {
                console.error("Errore durante l'aggiornamento della banda:", error);
                // Non aggiungiamo il modello al set in caso di errore per permettere retry
            }
        };

        // Ritarda l'aggiornamento per permettere l'inizializzazione
        setTimeout(updateViewerBandwidth, 10);
    }, [initialModel, lock, idViewer, modelFromCache]);

    // === EFFECTS PER LA GESTIONE DELLA SESSIONE FIRESTORE ===

    /**
     * Effect: Crea la sessione in Firestore quando i dati sono pronti
     */
    useEffect(() => {
        if (!session.deviceId || !session.catalogProductRefsDoc || isCatalogExpired) return;

        let alreadyCreated = false;
        const addSession = async () => {
            if (alreadyCreated) return;
            alreadyCreated = true;
            try {
                const sessioneRef = doc(collection(db, "Session_3DViewer"));
                await setDoc(sessioneRef, session);
                setSessionRef(sessioneRef);
            } catch (error) {
                console.error("Errore durante la creazione della sessione:", error);
                setError("An error occurred while creating the session");
            }
        };
        addSession();
    }, [session.deviceId, session.catalogProductRefsDoc, isCatalogExpired]);

    /**
     * Effect: Aggiornamenti periodici della sessione ogni 5 secondi
     */
    useEffect(() => {
        if (!sessionRef || isSessionExpired || isCatalogExpired) return;

        const interval = setInterval(() => {
            const date = new Date();
            const totalTimeDiff = Math.floor((date.getTime() - initialTime.getTime()) / 1000);
            const currentVariantTimeDiff = Math.floor((date.getTime() - initialModelTime.getTime()) / 1000);

            setSession(prevSession => {
                const updatedSession = {
                    ...prevSession,
                    totalTime: totalTimeDiff,
                    sessionEndDate: formatSessionDate(date),
                    list_viewed_products: prevSession.list_viewed_products.map((item) => {
                        const updatedVariants = item.list_tried_variants.map((variant) => {
                            // Aggiorna solo il tempo della variante attualmente visualizzata
                            if (variant.catalogVariantRefs === initialModel) {
                                return {
                                    ...variant,
                                    totalSeconds: (variant.totalSeconds || 0) + currentVariantTimeDiff,
                                };
                            }
                            return variant;
                        });

                        return {
                            ...item,
                            totalSeconds: totalTimeDiff,
                            list_tried_variants: updatedVariants,
                        };
                    }),
                };

                // Aggiorna Firestore
                updateDoc(sessionRef, updatedSession).catch((error) => {
                    console.error("Error updating document: ", error);
                });

                return updatedSession;
            });

            // Reset del tempo per la variante corrente
            setInitiaModellTime(new Date());
        }, 5000);

        return () => clearInterval(interval);
    }, [sessionRef, initialTime, isSessionExpired, initialModel, initialModelTime, isCatalogExpired]);

    /**
     * Effect: Gestione dell'evento beforeunload per salvare la sessione all'uscita
     */
    useEffect(() => {
        const handleUnload = () => {
            if (!sessionRef) return;

            const date = new Date();
            const totalTimeDiff = Math.floor((date.getTime() - initialTime.getTime()) / 1000);
            const currentVariantTimeDiff = Math.floor((date.getTime() - initialModelTime.getTime()) / 1000);

            const updatedSession = {
                ...session,
                totalTime: totalTimeDiff,
                sessionEndDate: formatSessionDate(date),
                list_viewed_products: session.list_viewed_products.map((item) => {
                    const updatedVariants = item.list_tried_variants.map((variant) => {
                        if (variant.catalogVariantRefs === initialModel) {
                            return {
                                ...variant,
                                totalSeconds: (variant.totalSeconds || 0) + currentVariantTimeDiff,
                            };
                        }
                        return variant;
                    });

                    return {
                        ...item,
                        totalSeconds: totalTimeDiff,
                        list_tried_variants: updatedVariants,
                    };
                }),
            };

            updateDoc(sessionRef, updatedSession)
                .catch((error) => {
                    console.error("Errore nell'aggiornamento della sessione:", error);
                });
        };

        window.addEventListener("beforeunload", handleUnload);
        if (isWebView) return;
        return () => {
            window.removeEventListener("beforeunload", handleUnload);
        };
    }, [session, sessionRef, initialTime, initialModelTime, initialModel, isWebView]);

    // === EVENT HANDLERS ===

    /**
     * Gestisce il cambio di variante con tracciamento dei tempi
     */
    const selecteModelInView = async (project, modelId) => {
        setModelHistory([...modelHistory, document.querySelector("model-viewer")?.src]);
        
        // Recupera i dati della nuova variante per ottenere size.size
        let newVariantSize = "";
        try {
            const variantRef = doc(collection(db, "CataloguesVariants"), modelId);
            const variantSnap = await getDoc(variantRef);
            if (variantSnap.exists()) {
                const variantData = variantSnap.data();
                newVariantSize = variantData?.size?.size || "";
            }
        } catch (error) {
            console.error("Errore nel recupero dei dati della variante:", error);
        }
        
        // Traccia il tempo di visualizzazione della variante precedente
        if (initialModel && session.list_viewed_products.length > 0) {
            const currentTime = new Date();
            const timeDiff = Math.floor((currentTime.getTime() - initialModelTime.getTime()) / 1000);
            
            setSession(prevSession => {
                const updatedViewedProducts = [...prevSession.list_viewed_products];
                let updatedSizeRefs = [...prevSession.list_size_refs];
                
                if (updatedViewedProducts[0]) {
                    const existingVariantIndex = updatedViewedProducts[0].list_tried_variants.findIndex(
                        variant => variant.catalogVariantRefs === initialModel
                    );
                    
                    if (existingVariantIndex !== -1) {
                        // Aggiorna il tempo della variante esistente
                        updatedViewedProducts[0].list_tried_variants[existingVariantIndex] = {
                            ...updatedViewedProducts[0].list_tried_variants[existingVariantIndex],
                            totalSeconds: updatedViewedProducts[0].list_tried_variants[existingVariantIndex].totalSeconds + timeDiff
                        };
                    }
                    
                    // Aggiungi la nuova variante se non esiste già
                    const newVariantExists = updatedViewedProducts[0].list_tried_variants.some(
                        variant => variant.catalogVariantRefs === modelId
                    );
                    
                    if (!newVariantExists) {
                        updatedViewedProducts[0].list_tried_variants.push({
                            catalogVariantRefs: modelId,
                            timeStart: currentTime,
                            totalSeconds: 0,
                            sizeRef: newVariantSize,
                        });
                        
                        // Aggiungi il size alla lista se non esiste già
                        if (newVariantSize && !updatedSizeRefs.includes(newVariantSize)) {
                            updatedSizeRefs.push(newVariantSize);
                        }
                    }
                }
                
                return {
                    ...prevSession,
                    list_viewed_products: updatedViewedProducts,
                    list_size_refs: updatedSizeRefs
                };
            });
        }
        
        // Aggiorna il tempo di inizio per la nuova variante
        setInitiaModellTime(new Date());
        setInitialModel(modelId);
    };

    /**
     * Wrapper per setSelectedId
     */
    const setSelectedId = async (modelId) => {
        await selecteModelInView(project[0], modelId);
    };

    /**
     * Handler per la selezione del modello
     */
    const handleSelectModel = async (modelIdCard) => {
        await selecteModelInView(project[0], modelIdCard);
    };

    /**
     * Sblocca il visualizzatore se protetto da password
     */
    const unlock = () => {
        setLock(false);
    };

    /**
     * Mostra avviso per browser non supportati
     */
    const handleBrowserWarning = () => {
        notification.warning({
            message: "Browser Not Supported",
            description:
                "The Facebook browser does not support 3D models and augmented reality. Please try another browser.",
            placement: "top",
        });
    };

    // === EFFECTS PER PROPS E CALLBACKS ===

    /**
     * Effect: Notifica il parent component del cambio di variante
     */
    useEffect(() => {
        if (initialModel) {
            setCurrentVariant(initialModel);
            setVariantData(initialModel);
        }
    }, [initialModel]);

    /**
     * Effect: Notifica il parent component della lista varianti
     */
    useEffect(() => {
        if (listVariant?.length) {
            _setListVariant(listVariant);
        }
    }, [listVariant]);

    /**
     * Effect: Gestisce il caricamento completato
     */
    useEffect(() => {
        if (initialModel || error) {
            const timer = setTimeout(() => {
                setIsLoading(false);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [initialModel, error]);

    /**
     * Imposta i dati della variante - potrebbe essere semplificato
     */
    const setVariantData = async (initialModel) => {
        try {
            // Ottieni il riferimento alla variante
            const variantRef = doc(
                collection(db, "3DViewers", tViewer, "Products", product, "variants"),
                initialModel
            );
            const variantSnap = await getDoc(variantRef);

            const variantData = variantSnap.exists() ? variantSnap.data() : null;

            if (variantData?.shouldApply) {
                return dispatch(brandViewerActions.setSelectedProject(variantData));
            }

            // Ottieni il riferimento al modello ARTemplate
            const modelRef = doc(
                collection(db, "3DViewers", tViewer, "Products", params?.TokenCatalogProduct, "template"),
                "ARTemplate"
            );
            const modelSnap = await getDoc(modelRef);

            const modelData = modelSnap.exists() ? modelSnap.data() : null;

            if (modelData?.shouldApply) {
                return dispatch(brandViewerActions.setSelectedProject(modelData));
            }

            // Ottieni i dati del visualizzatore principale
            const viewerRef = doc(collection(db, "3DViewers"), tViewer);
            const viewerSnap = await getDoc(viewerRef);

            const rootData = viewerSnap.exists() ? viewerSnap.data() : null;

            if (rootData) {
                return dispatch(brandViewerActions.setSelectedProject(rootData));
            }

        } catch (error) {
            console.error("Errore durante il recupero dei dati della variante:", error);
        }
    };

    // Callback per gestire lo stato della cache del modello
    const handleModelCacheStatus = (fromCache) => {
        setModelFromCache(fromCache);
        if (fromCache !== null) {
            console.log(fromCache ? "🟢 Modello caricato da cache" : "🔵 Modello scaricato da Firebase Storage");
        }
    };

    // === RENDER ===
    return (
        <div>
            {/* Controllo accesso con password */}
            {lock ? (
                <Password unlock={unlock} password={pwd} />
            ) : (
                <ModelContainerFrontEnd
                    modelSelected={modelInView}
                    project={project[0]}
                    isEditModeOn={isEditModeOn}
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                >
                    {/* Menu superiore */}
                    {positionMenu === "top" && (
                        <MenuContainer
                            style={"TOP"}
                            menu={project[0]?.menuType}
                            childCount={listVariant.length}
                            key={project[0]?.menuType}
                        >
                            {listVariant.map((modelIdCard, i) => (
                                <CardModelView
                                    key={i}
                                    modelId={modelIdCard}
                                    selected={initialModel}
                                    project={project[0]}
                                    selectModel={() => handleSelectModel(modelIdCard)}
                                    cardStyle={project[0]?.menuType}
                                />
                            ))}
                        </MenuContainer>
                    )}

                    {/* Menu sinistro */}
                    {positionMenu === "left" && (
                        <MenuContainer
                            style={"LEFT"}
                            menu={project[0]?.menuType}
                            key={project[0]?.menuType}
                        >
                            {listVariant.map((modelIdCard, i) => (
                                <CardModelView
                                    key={i}
                                    modelId={modelIdCard}
                                    selected={initialModel}
                                    project={project[0]}
                                    selectModel={() => handleSelectModel(modelIdCard)}
                                    cardStyle={project[0]?.menuType}
                                />
                            ))}
                        </MenuContainer>
                    )}

                    {/* Visualizzatori 3D - solo se non è Facebook Browser */}
                    {!error && !navigator.userAgent.match(/FBAV/i) && (
                        <>
                            {useLegacyViewer ? (
                                <ProjectModelViewer
                                    multiViewer
                                    title={project.title}
                                    initialModel={initialModel}
                                    project={project[0]}
                                    paramsId={params.TokenCatalogProduct}
                                    urlView={`https://studio.arshades.it/Arshades3ds/${params.TokenViewer}/glasses/${params.TokenCatalogProduct}`}
                                    viewerPage
                                    isEditModeOn={isEditModeOn}
                                />
                            ) : (
                                <ThreeJSViewer
                                    multiViewer
                                    title={project.title}
                                    initialModel={initialModel}
                                    paramsId={params.TokenCatalogProduct}
                                    urlView={`https://studio.arshades.it/Arshades3ds/${params.TokenViewer}/glasses/${params.TokenCatalogProduct}`}
                                    viewerPage
                                    isEditModeOn={isEditModeOn}
                                    onModelCacheStatus={handleModelCacheStatus}
                                />
                            )}
                        </>
                    )}

                    {/* Messaggio di errore - mostrato solo dopo il caricamento */}
                    {!isLoading && error && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            zIndex: 10
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                padding: '30px',
                                maxWidth: '80%',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                textAlign: 'center'
                            }}>
                                <img src={logoSorry} style={{ width: "30%", padding: "1px", marginBottom: "3px" }} alt="Not supported" />
                                <div style={{
                                    marginBottom: '15px',
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    color: '#343a40'
                                }}>
                                    {isCatalogExpired ? 'Sorry' : 'Model not available'}
                                </div>
                                <div style={{
                                    color: '#6c757d',
                                    maxWidth: '400px',
                                    lineHeight: '1.5'
                                }}>
                                    {error}
                                    <br /><br />
                                    {isCatalogExpired && 'If you are the owner, please contact us.'}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Fallback per Facebook Browser */}
                    {navigator.userAgent.match(/FBAV/i) &&
                        !navigator.userAgent.match(/(iPod|iPhone|iPad)/) && (
                            <>
                                <img
                                    src={modelInView?.not_supported_image}
                                    style={{ width: "100%" }}
                                    alt="Not supported"
                                />
                                {handleBrowserWarning()}
                            </>
                        )}

                    {/* Menu inferiore */}
                    {positionMenu === "bottom" && (
                        <MenuContainer
                            style={"BOTTOM"}
                            menu={project[0]?.menuType}
                            key={project[0]?.menuType}
                            childCount={listVariant.length}
                            selectedId={initialModel}
                            setSelectedId={setSelectedId}
                        >
                            {listVariant.map((modelIdCard, i) => (
                                <CardModelView
                                    key={i}
                                    modelId={modelIdCard}
                                    selected={initialModel}
                                    project={project[0]}
                                    selectModel={() => handleSelectModel(modelIdCard)}
                                    cardStyle={project[0]?.menuType}
                                />
                            ))}
                        </MenuContainer>
                    )}

                    {/* Menu destro */}
                    {positionMenu === "right" && (
                        <MenuContainer
                            style={"RIGHT"}
                            menu={project[0]?.menuType}
                            key={project[0]?.menuType}
                        >
                            {listVariant.map((modelIdCard, i) => (
                                <CardModelView
                                    key={i}
                                    modelId={modelIdCard}
                                    selected={initialModel}
                                    project={project[0]}
                                    selectModel={() => handleSelectModel(modelIdCard)}
                                    cardStyle={project[0]?.menuType}
                                />
                            ))}
                        </MenuContainer>
                    )}
                </ModelContainerFrontEnd>
            )}
        </div>
    );
};

export default ARShades3dsViewer;
